import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Phone, Mail, Clock, ArrowRight, Instagram, Facebook } from 'lucide-react';
import { SITE } from '../data/content';
import { PageHero } from '../components/PageHero';
import { Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Por favor llena tu nombre, correo y mensaje.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success('¡Gracias! Tu mensaje fue enviado.');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Algo salió mal. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full bg-brand-night border border-brand-line px-4 py-3 text-brand-cream placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-gold transition-colors';

  return (
    <div data-testid="contact-page" className="bg-brand-night">
      <PageHero
        testid="contact-hero"
        overline="¡Hola!"
        title="Contáctanos"
        subtitle="¿Preguntas, grupos grandes, prensa o solo quieres saludar? Escríbenos — nos encantará oírte."
      />

      <section className="mx-auto max-w-7xl px-6 md:px-12 pb-24 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        {/* Info */}
        <Reveal>
          <div className="space-y-8">
            <div className="flex gap-4">
              <span className="p-3 bg-brand-coal border border-brand-line text-brand-gold h-fit"><MapPin size={20} /></span>
              <div>
                <p className="font-display font-semibold text-lg text-brand-cream">Encuéntranos</p>
                <p className="text-brand-cream/70 mt-1">{SITE.address}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="p-3 bg-brand-coal border border-brand-line text-brand-gold h-fit"><Phone size={20} /></span>
              <div>
                <p className="font-display font-semibold text-lg text-brand-cream">Llámanos</p>
                <a href={`tel:${SITE.phone}`} className="text-brand-cream/70 mt-1 block hover:text-brand-gold transition-colors">{SITE.phone}</a>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="p-3 bg-brand-coal border border-brand-line text-brand-gold h-fit"><Mail size={20} /></span>
              <div>
                <p className="font-display font-semibold text-lg text-brand-cream">Correo</p>
                <a href={`mailto:${SITE.email}`} className="text-brand-cream/70 mt-1 block hover:text-brand-gold transition-colors">{SITE.email}</a>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="p-3 bg-brand-coal border border-brand-line text-brand-gold h-fit"><Clock size={20} /></span>
              <div>
                <p className="font-display font-semibold text-lg text-brand-cream">Horario</p>
                <ul className="mt-1 space-y-1 text-brand-cream/70 text-sm">
                  {SITE.hours.map((h) => (
                    <li key={h.day} className="flex gap-6 justify-between max-w-xs"><span>{h.day}</span><span className="text-brand-muted">{h.time}</span></li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="p-3 border border-brand-line text-brand-cream hover:border-brand-gold hover:text-brand-gold transition-colors"><Instagram size={20} /></a>
              <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" data-testid="contact-facebook" className="p-3 border border-brand-line text-brand-cream hover:border-brand-gold hover:text-brand-gold transition-colors"><Facebook size={20} /></a>
            </div>

            <div className="relative bg-brand-coal border border-brand-line h-56 flex items-center justify-center mt-4 overflow-hidden">
              <div className="pattern-dots-warm absolute inset-0 opacity-60" aria-hidden="true" />
              <p className="relative text-brand-muted text-sm flex items-center gap-2"><MapPin size={16} /> Mapa — {SITE.address}</p>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <form onSubmit={submit} data-testid="contact-form" className="bg-brand-coal border border-brand-line p-8 md:p-10 space-y-5">
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-brand-cream">Envíanos un Mensaje</h2>
            <div className="serape-underline" />
            <div>
              <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Nombre *</label>
              <input data-testid="contact-name" value={form.name} onChange={update('name')} className={inputCls} placeholder="Tu nombre" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Correo *</label>
                <input data-testid="contact-email" type="email" value={form.email} onChange={update('email')} className={inputCls} placeholder="tu@correo.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Teléfono</label>
                <input data-testid="contact-phone" value={form.phone} onChange={update('phone')} className={inputCls} placeholder="(214) 555-0000" />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-brand-muted mb-2">Mensaje *</label>
              <textarea data-testid="contact-message" value={form.message} onChange={update('message')} rows={5} className={inputCls} placeholder="¿Cómo podemos ayudarte?" />
            </div>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="w-full inline-flex items-center justify-center gap-2 bg-brand-terracotta text-white px-7 py-4 text-sm uppercase tracking-widest font-medium hover:bg-brand-terracottaDark transition-colors disabled:opacity-60">
              {loading ? 'Enviando…' : <>Enviar Mensaje <ArrowRight size={16} /></>}
            </button>
          </form>
        </Reveal>
      </section>
    </div>
  );
};

export default ContactPage;
